import { useState, useEffect, useRef, forwardRef } from 'react';

/**
 * CircularGallery — 3D rotating card carousel (desktop).
 * Mobile: horizontal snap-scroll card list.
 */
const CircularGallery = forwardRef(
  ({ items, style, radius = 600, autoRotateSpeed = 0.025, ...props }, ref) => {
    const [isMobile, setIsMobile] = useState(false);
    const [rotation, setRotation] = useState(0);
    const [isScrolling, setIsScrolling] = useState(false);
    const scrollTimeoutRef = useRef(null);
    const animationFrameRef = useRef(null);
    const lastScrollY = useRef(window.scrollY);

    /* detect mobile */
    useEffect(() => {
      const check = () => setIsMobile(window.innerWidth < 768);
      check();
      window.addEventListener('resize', check);
      return () => window.removeEventListener('resize', check);
    }, []);

    /* Scroll-based rotation (desktop only) */
    useEffect(() => {
      if (isMobile) return;
      const handleScroll = () => {
        setIsScrolling(true);
        clearTimeout(scrollTimeoutRef.current);
        const delta = window.scrollY - lastScrollY.current;
        lastScrollY.current = window.scrollY;
        setRotation((prev) => prev + delta * 0.15);
        scrollTimeoutRef.current = setTimeout(() => setIsScrolling(false), 200);
      };
      window.addEventListener('scroll', handleScroll, { passive: true });
      return () => {
        window.removeEventListener('scroll', handleScroll);
        clearTimeout(scrollTimeoutRef.current);
      };
    }, [isMobile]);

    const [isVisible, setIsVisible] = useState(false);

    /* pause when out of viewport */
    useEffect(() => {
      const el = ref?.current || animationFrameRef.current;
      const observer = new IntersectionObserver(
        ([entry]) => setIsVisible(entry.isIntersecting),
        { threshold: 0.05 }
      );
      // observe the outer wrapper via a dedicated ref below
      return () => observer.disconnect();
    }, []);

    /* pause when tab hidden */
    useEffect(() => {
      const onVisibility = () => {
        if (document.hidden) setIsScrolling(true); // stops RAF accumulation
      };
      document.addEventListener('visibilitychange', onVisibility);
      return () => document.removeEventListener('visibilitychange', onVisibility);
    }, []);

    /* Auto-rotation (desktop only, pause when hidden) */
    useEffect(() => {
      if (isMobile) return;
      let rafId;
      const tick = () => {
        if (!isScrolling && !document.hidden) {
          setRotation((prev) => prev + autoRotateSpeed);
        }
        rafId = requestAnimationFrame(tick);
      };
      rafId = requestAnimationFrame(tick);
      return () => cancelAnimationFrame(rafId);
    }, [isMobile, isScrolling, autoRotateSpeed]);

    const anglePerItem = 360 / items.length;

    /* ── MOBILE: horizontal snap scroll ─────────────────────────────────── */
    if (isMobile) {
      return (
        <div ref={ref} style={{ width: '100%', overflowX: 'auto', WebkitOverflowScrolling: 'touch', ...style }} {...props}>
          <div style={{
            display: 'flex', gap: '1rem',
            padding: '1rem 1.25rem 2rem',
            scrollSnapType: 'x mandatory',
            overflowX: 'auto',
            WebkitOverflowScrolling: 'touch',
          }}>
            {items.map((item, i) => (
              <div key={i} style={{
                flexShrink: 0,
                width: '72vw', maxWidth: '280px',
                height: '340px',
                scrollSnapAlign: 'start',
                borderRadius: '14px',
                overflow: 'hidden',
                position: 'relative',
                boxShadow: '0 12px 40px rgba(0,0,0,0.5)',
                border: '1px solid rgba(255,255,255,0.08)',
                background: 'rgba(10,10,12,0.7)',
              }}>
                <img
                  src={item.photo.url}
                  alt={item.photo.text}
                  loading="lazy"
                  style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', objectPosition: item.photo.pos || 'center' }}
                />
                <div style={{
                  position: 'absolute', bottom: 0, left: 0, width: '100%',
                  padding: '2rem 1.1rem 1.1rem',
                  background: 'linear-gradient(to top, rgba(0,0,0,0.88), transparent)',
                  color: '#fff',
                }}>
                  <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: 700, letterSpacing: '-0.01em', fontFamily: 'var(--font-headline, "Space Grotesk", sans-serif)' }}>
                    {item.common}
                  </h3>
                  <em style={{ display: 'block', fontSize: '0.7rem', fontStyle: 'normal', opacity: 0.6, marginTop: '0.2rem', letterSpacing: '0.05em', fontFamily: 'var(--font-body, "Inter", sans-serif)' }}>
                    {item.binomial}
                  </em>
                </div>
              </div>
            ))}
          </div>
          {/* Scroll hint */}
          <p style={{
            textAlign: 'center', fontSize: '0.58rem',
            fontFamily: "'Space Mono', monospace", letterSpacing: '0.18em',
            color: 'rgba(255,255,255,0.2)', textTransform: 'uppercase',
            marginBottom: '0.5rem',
          }}>← GESER →</p>
        </div>
      );
    }

    /* ── DESKTOP: 3D circular gallery ───────────────────────────────────── */
    return (
      <div
        ref={ref}
        role="region"
        aria-label="Circular 3D Gallery"
        style={{
          position: 'relative', width: '100%', height: '100%',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          perspective: '2000px',
          ...style,
        }}
        {...props}
      >
        <div style={{
          position: 'relative', width: '100%', height: '100%',
          transform: `rotateY(${rotation}deg)`,
          transformStyle: 'preserve-3d',
          willChange: 'transform',
        }}>
          {items.map((item, i) => {
            const itemAngle = i * anglePerItem;
            const totalRotation = rotation % 360;
            const relativeAngle = (itemAngle + totalRotation + 360) % 360;
            const normalizedAngle = Math.abs(relativeAngle > 180 ? 360 - relativeAngle : relativeAngle);
            const opacity = Math.max(0.2, 1 - normalizedAngle / 180);
            return (
              <div
                key={item.photo.url + i}
                role="group"
                aria-label={item.common}
                style={{
                  position: 'absolute',
                  width: '260px', height: '360px',
                  transform: `rotateY(${itemAngle}deg) translateZ(${radius}px)`,
                  left: '50%', top: '50%',
                  marginLeft: '-130px', marginTop: '-180px',
                  opacity,
                  transition: 'opacity 0.3s linear',
                }}
              >
                <div style={{
                  position: 'relative', width: '100%', height: '100%',
                  borderRadius: '14px', overflow: 'hidden',
                  boxShadow: '0 24px 60px rgba(0,0,0,0.55)',
                  border: '1px solid rgba(255,255,255,0.09)',
                  background: 'rgba(10,10,12,0.7)',
                  backdropFilter: 'blur(14px)',
                  WebkitBackdropFilter: 'blur(14px)',
                }}>
                  <img
                    src={item.photo.url}
                    alt={item.photo.text}
                    loading="lazy"
                    style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', objectPosition: item.photo.pos || 'center' }}
                  />
                  <div style={{
                    position: 'absolute', bottom: 0, left: 0, width: '100%',
                    padding: '2rem 1.25rem 1.25rem',
                    background: 'linear-gradient(to top, rgba(0,0,0,0.88), transparent)',
                    color: '#fff',
                  }}>
                    <h3 style={{ margin: 0, fontSize: '1.1rem', fontWeight: 700, letterSpacing: '-0.01em', fontFamily: 'var(--font-headline, "Space Grotesk", sans-serif)' }}>
                      {item.common}
                    </h3>
                    <em style={{ display: 'block', fontSize: '0.72rem', fontStyle: 'normal', opacity: 0.65, marginTop: '0.25rem', letterSpacing: '0.05em', fontFamily: 'var(--font-body, "Inter", sans-serif)' }}>
                      {item.binomial}
                    </em>
                    {item.photo.by && (
                      <span style={{ display: 'block', fontSize: '0.68rem', marginTop: '0.4rem', opacity: 0.45, fontFamily: 'var(--font-body, "Inter", sans-serif)' }}>
                        {item.photo.by}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  }
);

CircularGallery.displayName = 'CircularGallery';
export { CircularGallery };
